/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    X,
    ArrowRight,
    Menu,
  } from "lucide-react";
  import { Link } from "react-router-dom";
import Footer from '../components/Footer';
import Seo from '../components/Seo';

  // These are all the smaller components needed for the About page

// The TabButton component used in the navigation
const TabButton = ({ active, onClick, label, icon }) => (
    <button
      onClick={onClick}
      className={`px-4 py-2 rounded-lg text-sm md:text-base transition-colors flex items-center whitespace-nowrap ${
        active 
          ? 'bg-green-600 text-white' 
          : 'text-white/70 hover:text-white hover:bg-white/10'
      }`}
    >
      <span className="mr-2">{icon}</span>
      {label}
    </button>
  );
  
  // Tab content container with animations
  const TabContent = ({ children }) => (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3 }}
      className="min-h-[400px]"
    >
      {children}
    </motion.div>
  );
  
  // Component for feature highlights
  const Highlight = ({ icon, text }) => (
    <motion.div 
      className="flex items-start"
      whileHover={{ x: 3 }}
      transition={{ duration: 0.2 }}
    >
      <div className="text-green-500 mr-3 mt-0.5 flex-shrink-0">
        {icon}
      </div>
      <p className="text-white/90 text-sm">{text}</p>
    </motion.div>
  );
  
  // Component for game mechanics
  const Mechanic = ({ title, description }) => (
    <div className="bg-green-950/50 p-3 rounded-lg border border-green-800/30">
      <h4 className="font-medium text-green-300 mb-1">{title}</h4>
      <p className="text-sm text-white/80">{description}</p>
    </div>
  );
  
  // Team member card
  const TeamMember = ({ name, role, bio, delay }) => (
    <motion.div
      className="bg-slate-800/70 rounded-xl p-5 border border-slate-700"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      whileHover={{ y: -5, transition: { duration: 0.2 } }}
    >
      <div className="w-16 h-16 mx-auto bg-gradient-to-br from-green-500 to-emerald-600 rounded-full mb-4 flex items-center justify-center">
        <div className="w-14 h-14 rounded-full bg-slate-800 flex items-center justify-center">
          <UserIcon className="w-8 h-8 text-green-400" />
        </div>
      </div>
      <h3 className="font-bold text-lg mb-1">{name}</h3>
      <div className="text-green-400 text-sm mb-3">{role}</div>
      <p className="text-white/70 text-sm">{bio}</p>
    </motion.div>
  );
  
  // Icon components
  const StarIcon = (props) => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className={`w-5 h-5 ${props.className || ''}`}
    >
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
  
  const TrophyIcon = (props) => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className={`w-5 h-5 ${props.className || ''}`}
    >
      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path>
      <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"></path>
      <path d="M4 22h16"></path>
      <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"></path>
      <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"></path>
      <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"></path>
    </svg>
  );
  
  const GameIcon = (props) => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className={`w-5 h-5 ${props.className || ''}`}
    >
      <circle cx="12" cy="12" r="10"></circle>
      <path d="M12 8v8"></path>
      <path d="M8 12h8"></path>
    </svg>
  );
  
  const StoryIcon = (props) => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className={`w-5 h-5 ${props.className || ''}`}
    >
      <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20"></path>
    </svg>
  );
  
  const FeatIcon = (props) => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className={`w-5 h-5 ${props.className || ''}`}
    >
      <circle cx="12" cy="12" r="10"></circle>
      <path d="m16 12-4-4-4 4"></path>
      <path d="M12 16V8"></path>
    </svg>
  );
  
  const TeamIcon = (props) => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className={`w-5 h-5 ${props.className || ''}`}
    >
      <circle cx="9" cy="7" r="4"></circle>
      <path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"></path>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
      <path d="M21 21v-2a4 4 0 0 0-3-3.85"></path>
    </svg>
  );
  
  const UserIcon = (props) => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className={`w-5 h-5 ${props.className || ''}`}
    >
      <circle cx="12" cy="8" r="5"></circle>
      <path d="M20 21a8 8 0 1 0-16 0"></path>
    </svg>
  );
  
  const PlayIcon = (props) => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className={`w-5 h-5 ${props.className || ''}`}
    >
      <circle cx="12" cy="12" r="10"></circle>
      <polygon points="10 8 16 12 10 16 10 8"></polygon>
    </svg>
  );
  
  const BookIcon = (props) => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className={`w-5 h-5 ${props.className || ''}`}
    >
      <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20"></path>
    </svg>
  );
  
  // Feature Icons
  const TacticsIcon = () => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className="w-8 h-8"
    >
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
      <line x1="8" y1="21" x2="16" y2="21"></line>
      <line x1="12" y1="17" x2="12" y2="21"></line>
    </svg>
  );
  
  const ScoutingIcon = () => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className="w-8 h-8"
    >
      <circle cx="11" cy="11" r="8"></circle>
      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
    </svg>
  );
  
  const TrainingIcon = () => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className="w-8 h-8"
    >
      <path d="M18 8h1a4 4 0 0 1 0 8h-1"></path>
      <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"></path>
      <line x1="6" y1="1" x2="6" y2="4"></line>
      <line x1="10" y1="1" x2="10" y2="4"></line>
      <line x1="14" y1="1" x2="14" y2="4"></line>
    </svg>
  );
  
  const TransfersIcon = () => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className="w-8 h-8"
    >
      <line x1="12" y1="5" x2="12" y2="19"></line>
      <polyline points="19 12 12 19 5 12"></polyline>
    </svg>
  );
  
  const StadiumIcon = () => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className="w-8 h-8"
    >
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
      <line x1="16" y1="2" x2="16" y2="6"></line>
      <line x1="8" y1="2" x2="8" y2="6"></line>
      <line x1="3" y1="10" x2="21" y2="10"></line>
    </svg>
  );
  
  const FinancesIcon = () => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className="w-8 h-8"
    >
      <line x1="12" y1="2" x2="12" y2="22"></line>
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
    </svg>
  );

const About = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('game');
  const [showFeatures, setShowFeatures] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // Data for features section
  const features = [
    {
      icon: <TacticsIcon />,
      title: "Master Tactics",
      description: "Design game-winning formations, set player roles, and create tactical approaches for different match situations.",
      color: "bg-green-500"
    },
    {
      icon: <ScoutingIcon />,
      title: "Scouting Network",
      description: "Develop a global scouting system to discover hidden talents and future stars before your rivals spot them.",
      color: "bg-[var(--accent)]"
    },
    {
      icon: <TrainingIcon />,
      title: "Player Development",
      description: "Create training schedules, improve player attributes, and develop youth prospects into first-team stars.",
      color: "bg-amber-500"
    },
    {
      icon: <TransfersIcon />,
      title: "Transfer Mastery",
      description: "Negotiate contracts, buy and sell players, and build a squad that dominates domestically and in Europe.",
      color: "bg-[var(--surface-2)]0"
    },
    {
      icon: <StadiumIcon />,
      title: "Stadium Management",
      description: "Expand your stadium, improve facilities, and create the perfect environment for fans and players alike.",
      color: "bg-red-500"
    },
    {
      icon: <FinancesIcon />,
      title: "Financial Control",
      description: "Balance the books, secure sponsorships, and ensure your club remains financially stable while growing.",
      color: "bg-emerald-500"
    }
  ];

  // Trigger the feature animation after a short delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowFeatures(true);
    }, 800);
    
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 min-h-screen text-white">
    <Seo
      title="About My Football Game - Free Online Football Manager"
      description="Learn about My Football Game: a free browser-based football management simulator where you build your club, set tactics, trade players and compete globally."
      path="/about"
    />
    {/* Hero Section */}
    <div className="container mx-auto px-4 pt-8 pb-20">
      <header className="flex justify-between items-center py-4 mb-16 relative">
        <div className="flex items-center">
          <img
            src="/logo.png"
            alt="My Football Game Logo"
            className="w-10 h-10 object-contain mr-3"
          />

          <h1 className="text-2xl font-bold">
            My Football <span className="text-green-500">Game</span>
          </h1>
        </div>

        <nav>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden text-green-500"
          >
            {isOpen ? <X size={28} /> : <Menu size={28} />}
          </button>

          <ul
            className={`flex flex-col md:flex-row text-green-500 md:space-x-8 absolute md:static top-16 right-0 bg-white md:bg-transparent w-full md:w-auto p-4 md:p-0 shadow md:shadow-none transition-all duration-300 ${
              isOpen ? "block" : "hidden md:flex"
            }`}
          >
            <li>
              <a
                href="#features"
                className="block py-2 md:py-0 hover:text-green-400 transition-colors"
              >
                Features
              </a>
            </li>
            <li>
              <a
                href="#gameplay"
                className="block py-2 md:py-0 hover:text-green-400 transition-colors"
              >
                Gameplay
              </a>
            </li>
            <li>
              <a
                href="#video"
                className="block py-2 md:py-0 hover:text-green-400 transition-colors"
              >
                Video
              </a>
            </li>
            <li>
              <a
                href="#testimonials"
                className="block py-2 md:py-0 hover:text-green-400 transition-colors"
              >
                Testimonials
              </a>
            </li>
          </ul>
        </nav>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
        >
          <motion.h2
            className="text-4xl md:text-5xl font-bold mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            Build Your Club, <br />
            <span className="text-green-400">Dominate</span> The League
          </motion.h2>
          <motion.p
            className="text-lg text-gray-300 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
          >
            Experience the thrill of football management in this immersive simulation game. 
            Develop tactics, scout talent, build world-class facilities, and lead your club 
            to glory in domestic and international competitions.
          </motion.p>
          <motion.div
            className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
          >
            <Link to="/register">
              <motion.button
                className="px-8 py-3 bg-green-600 rounded-lg font-medium text-lg flex items-center justify-center hover:bg-green-700 transition-colors w-full sm:w-auto"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Play Now <ArrowRight className="ml-2 h-5 w-5" />
              </motion.button>
            </Link>
            <Link to="/login">
              <motion.button
                className="px-8 py-3 bg-transparent border border-white/30 rounded-lg font-medium text-lg flex items-center justify-center hover:bg-white/10 transition-colors w-full sm:w-auto"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Login
              </motion.button>
            </Link>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="relative hidden lg:block"
        >
          {/* Stadium illustration */}
          <div className="relative h-96 w-full">
            {/* Main stadium */}
            <motion.div
              className="absolute left-1/2 transform -translate-x-1/2 bottom-0 w-80 h-60 bg-gradient-to-b from-green-800 to-green-900 rounded-t-lg shadow-xl"
              initial={{ height: 0 }}
              animate={{ height: 240 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              {/* Stadium details */}
              <div className="relative w-full h-full">
                {/* Field */}
                <motion.div
                  className="absolute top-10 left-1/2 transform -translate-x-1/2 w-64 h-36 bg-green-500 rounded"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.9 }}
                >
                  {/* Field markings */}
                  <div className="absolute inset-2 border-2 border-white/60 rounded"></div>
                  <div className="absolute left-1/2 top-0 bottom-0 w-px bg-white/60 transform -translate-x-1/2"></div>
                  <div className="absolute left-1/2 top-1/2 w-16 h-16 border-2 border-white/60 rounded-full transform -translate-x-1/2 -translate-y-1/2"></div>
                </motion.div>
                
                {/* Stands */}
                <motion.div
                  className="absolute top-0 left-0 right-0 h-10 bg-slate-700 rounded-t-lg"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.8 }}
                >
                  {/* Crowd */}
                  <div className="flex h-full">
                    {[...Array(20)].map((_, i) => (
                      <motion.div
                        key={i}
                        className="flex-1 h-full bg-slate-600"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: Math.random() * 0.6 + 0.4 }}
                        transition={{ delay: 1 + i * 0.02 }}
                      />
                    ))}
                  </div>
                </motion.div>
                
                {/* Side stands */}
                <motion.div
                  className="absolute top-10 left-0 w-8 bottom-0 bg-slate-700"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.85 }}
                />
                <motion.div
                  className="absolute top-10 right-0 w-8 bottom-0 bg-slate-700"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.85 }}
                />
              </div>
            </motion.div>

            {/* Stadium lights */}
            <motion.div
              className="absolute left-10 bottom-0 w-6 h-64 bg-slate-700"
              initial={{ height: 0 }}
              animate={{ height: 256 }}
              transition={{ duration: 0.7, delay: 0.7 }}
            >
              <motion.div
                className="absolute top-0 left-0 right-0 h-6 w-6 bg-yellow-100"
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.8 }}
                transition={{ delay: 1.1 }}
              />
            </motion.div>
            
            <motion.div
              className="absolute right-10 bottom-0 w-6 h-64 bg-slate-700"
              initial={{ height: 0 }}
              animate={{ height: 256 }}
              transition={{ duration: 0.7, delay: 0.7 }}
            >
              <motion.div
                className="absolute top-0 left-0 right-0 h-6 w-6 bg-yellow-100"
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.8 }}
                transition={{ delay: 1.1 }}
              />
            </motion.div>

            {/* Stars/lights in the sky */}
            {[...Array(20)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-1 h-1 bg-white rounded-full"
                style={{
                  top: `${Math.random() * 40}%`,
                  left: `${Math.random() * 100}%`,
                }}
                initial={{ opacity: 0 }}
                animate={{ opacity: [0.2, 1, 0.2] }}
                transition={{
                  repeat: Infinity,
                  duration: 1 + Math.random() * 2,
                  delay: Math.random() * 2,
                }}
              />
            ))}
          </div>
        </motion.div>
      </div>

      {/* Stats */}
      <div className="mt-24 py-8 px-6 bg-slate-800/50 rounded-xl backdrop-blur-sm">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
          >
            <h3 className="text-4xl font-bold text-green-400">50+</h3>
            <p className="text-gray-300 mt-2">Leagues & Competitions</p>
          </motion.div>
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
          >
            <h3 className="text-4xl font-bold text-green-400">10K+</h3>
            <p className="text-gray-300 mt-2">Players</p>
          </motion.div>
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1 }}
          >
            <h3 className="text-4xl font-bold text-green-400">25</h3>
            <p className="text-gray-300 mt-2">Training Methods</p>
          </motion.div>
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1 }}
          >
            <h3 className="text-4xl font-bold text-green-400">∞</h3>
            <p className="text-gray-300 mt-2">Tactical Possibilities</p>
          </motion.div>
        </div>
      </div>
    </div>

      {/* Tab Navigation */}
      <div className="mx-auto max-w-6xl px-4">
        <motion.div 
          className="flex justify-center mb-8 border-b border-white/10 pb-1 gap-1 md:gap-3 overflow-x-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <TabButton 
            active={activeTab === 'game'} 
            onClick={() => setActiveTab('game')}
            label="About the Game"
            icon={<GameIcon />}
          />
          <TabButton 
            active={activeTab === 'story'} 
            onClick={() => setActiveTab('story')}
            label="The Story"
            icon={<StoryIcon />}
          />
          <TabButton 
            active={activeTab === 'features'} 
            onClick={() => setActiveTab('features')}
            label="Features"
            icon={<FeatIcon />}
          />
        </motion.div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'game' && (
            <TabContent key="game">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <h2 className="text-2xl md:text-3xl font-bold mb-4 text-green-400">Welcome to the Ultimate Football Management Simulation</h2>
                  <p className="mb-4 text-white/80">
                    My Football Game is a comprehensive football management simulator where you'll build and run a professional club while competing for titles and glory.
                  </p>
                  <p className="mb-4 text-white/80">
                    From humble beginnings with a small team and basic facilities, you'll develop your club with state-of-the-art training complexes, world-class scouting networks, and a stadium that rivals the greatest in the world!
                  </p>
                  <p className="mb-4 text-white/80">
                    Every decision matters - from tactics and team selection to transfers and youth development. Can you build a footballing dynasty and lead your club to domestic and European success?
                  </p>

                  <motion.div 
                    className="flex gap-4 mt-6"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                  >
                    <button
                      onClick={() => navigate('/register')} 
                      className="px-6 py-3 bg-green-600 hover:bg-green-700 rounded-lg font-medium transition-colors shadow-lg flex items-center"
                    >
                      <PlayIcon className="mr-2" />
                      Start Managing
                    </button>
                    <button
                      onClick={() => setActiveTab('features')} 
                      className="px-6 py-3 bg-white/10 hover:bg-white/20 rounded-lg font-medium transition-colors flex items-center"
                    >
                      <FeatIcon className="mr-2" />
                      See Features
                    </button>
                  </motion.div>
                </motion.div>

                <motion.div
                  className="p-4 bg-gradient-to-br from-green-900/50 to-emerald-900/50 rounded-2xl border border-green-700/30 shadow-lg relative overflow-hidden"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <div className="absolute top-0 left-0 w-full h-full bg-green-600/10 
                    bg-[radial-gradient(circle_at_center,rgba(59,246,130,0.1)_0,rgba(59,246,130,0)_70%)]" />
                    
                  <h3 className="text-xl font-bold mb-4 text-white flex items-center">
                    <TrophyIcon className="mr-2 text-yellow-500" />
                    Game Highlights
                  </h3>
                  
                  <ul className="space-y-3">
                    <Highlight icon={<StarIcon />} text="Comprehensive player attributes and development system" />
                    <Highlight icon={<StarIcon />} text="Advanced tactical creator with positional roles" />
                    <Highlight icon={<StarIcon />} text="Global scouting network with realistic player valuations" />
                    <Highlight icon={<StarIcon />} text="Dynamic squad dynamics and player personalities" />
                    <Highlight icon={<StarIcon />} text="Authentic transfer and contract negotiations" />
                  </ul>
                  
                  <motion.div 
                    className="mt-4 pt-4 border-t border-green-700/30"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.8 }}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-white/80">Player Rating</span>
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <StarIcon 
                            key={i} 
                            className={`w-4 h-4 ${i < 4.5 ? 'text-yellow-400' : 'text-white/30'}`} 
                            fill={i < 4.5 ? 'currentColor' : 'none'} 
                          />
                        ))}
                        <span className="ml-1 text-sm font-medium text-white/90">4.8</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-sm text-white/80">Downloads</span>
                      <span className="text-sm font-medium text-white/90">500K+</span>
                    </div>
                  </motion.div>
                </motion.div>
              </div>
            </TabContent>
          )}

          {activeTab === 'story' && (
            <TabContent key="story">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
                <motion.div 
                  className="md:col-span-2"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <h2 className="text-2xl md:text-3xl font-bold mb-4 text-green-400">The My Football Game Story</h2>
                  
                  <div className="prose prose-lg prose-invert max-w-none opacity-90">
                    <p>
                      In the competitive world of professional football, opportunity awaits. The beautiful game has evolved into a global phenomenon, with billions of fans, lucrative TV deals, and clubs becoming international brands.
                    </p>
                    
                    <p>
                      As an ambitious manager with dreams of building a footballing dynasty, you've accepted a role at a club with potential. It might not be much yet—just a decent stadium, some promising players, and loyal fans—but you see what it could become.
                    </p>
                    
                    <p>
                      The timing couldn't be better. Football is changing, with new technologies, tactical innovations, and global scouting networks transforming how clubs operate. The demand for success is higher than ever, with chairmen and fans expecting immediate results.
                    </p>
                    
                    <p>
                      But you're not the only manager with ambition. Rivals across the league and continent are strengthening their squads, improving their facilities, and hiring the best staff. The competition for trophies, players, and prestige is fiercer than ever.
                    </p>
                    
                    <p>
                      Will you create a tactical masterpiece that dominates possession? Build a fortress with an impenetrable defense? Or perhaps focus on lightning-fast counter-attacks that devastate opponents?
                    </p>
                    
                    <p>
                      The choice is yours, but one thing is certain: in the world of My Football Game, only the most strategic, adaptable, and visionary managers will lift the biggest trophies. Are you ready to build your legacy and dominate the beautiful game?
                    </p>
                  </div>
                </motion.div>
                
                <motion.div
                  className="hidden md:block md:col-span-1 relative"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 }}
                >
                  <div className="sticky top-10">
                    <div className="rounded-2xl bg-gradient-to-br from-green-900/50 to-emerald-900/50 border border-green-700/30 p-5 shadow-lg">
                      <h3 className="text-lg font-bold mb-3 flex items-center text-green-300">
                        <BookIcon className="mr-2" />
                        Gameplay Setting
                      </h3>
                      
                      <div className="space-y-4">
                        <div>
                          <h4 className="text-green-300 font-medium mb-1">Competitions</h4>
                          <p className="text-sm text-white/80">50+ authentic leagues from around the world, with domestic cups, continental competitions, and international tournaments.</p>
                        </div>
                        
                        <div>
                          <h4 className="text-green-300 font-medium mb-1">Time Period</h4>
                          <p className="text-sm text-white/80">Present day with a 30+ year simulation span. Progress through seasons with youth development, retirements, and regeneration.</p>
                        </div>
                        
                        <div>
                          <h4 className="text-green-300 font-medium mb-1">Competition</h4>
                          <p className="text-sm text-white/80">Over 500 realistic AI managers competing for jobs, players, and trophies, each with unique tactical preferences and personalities.</p>
                        </div>
                        
                        <div>
                          <h4 className="text-green-300 font-medium mb-1">Events</h4>
                          <p className="text-sm text-white/80">Dynamic events including financial takeovers, stadium disasters, player controversies, and unexpected board interventions that impact your career.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </TabContent>
          )}
          
          {activeTab === 'features' && (
            <TabContent key="features">
              <motion.h2 
                className="text-2xl md:text-3xl font-bold mb-6 text-green-400 text-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                Game Features
              </motion.h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {features.map((feature, index) => (
                  <motion.div
                    key={feature.title}
                    className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-slate-700 p-5 shadow-lg relative overflow-hidden group"
                    initial={{ opacity: 0, y: 20 }}
                    animate={showFeatures ? { 
                      opacity: 1, 
                      y: 0,
                      transition: { delay: 0.2 + index * 0.1 }
                    } : {}}
                    whileHover={{ 
                      y: -5,
                      transition: { duration: 0.2 }
                    }}
                  >
                    <div className={`w-16 h-16 ${feature.color} rounded-2xl mb-4 flex items-center justify-center shadow-lg relative group-hover:scale-110 transition-transform`}>
                      <div className="text-white">
                        {feature.icon}
                      </div>
                    </div>
                    <h3 className="text-xl font-bold mb-2 text-white">{feature.title}</h3>
                    <p className="text-white/80 text-sm leading-relaxed">{feature.description}</p>
                    
                    {/* Decorative elements */}
                    <div className="absolute -bottom-3 -right-3 w-20 h-20 bg-gradient-to-br from-white/0 to-white/5 rounded-full" />
                    <div className="absolute -bottom-4 -right-4 w-32 h-32 border border-white/10 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
                  </motion.div>
                ))}
              </div>
              
              <motion.div 
                className="mt-12 bg-gradient-to-r from-green-900/50 to-emerald-900/50 rounded-2xl p-6 border border-green-500/20 max-w-4xl mx-auto"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
              >
                <h3 className="text-xl font-bold mb-4 text-center text-white">Advanced Game Mechanics</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="space-y-4">
                    <Mechanic 
                      title="Player Development System" 
                      description="Complex player growth algorithms influenced by training, match time, mentoring, facilities, and hidden attributes like professionalism."
                    />
                    <Mechanic 
                      title="Team Dynamics" 
                      description="Players form relationships, develop leadership qualities, and respond differently to your management style based on their personalities."
                    />
                  </div>
                  <div className="space-y-4">
                    <Mechanic 
                      title="Transfer Market AI" 
                      description="Realistic transfer negotiations with clubs and agents who remember past interactions and adjust strategies accordingly."
                    />
                    <Mechanic 
                      title="Media Simulation" 
                      description="Dynamic press conferences, interviews, and media reports that influence player morale, fan expectations, and board confidence."
                    />
                    <Mechanic 
                      title="Financial Ecosystem" 
                      description="Detailed financial modeling including sponsorships, ticket pricing, merchandise, TV rights, and infrastructure investments."
                    />
                  </div>
                </div>
              </motion.div>
            </TabContent>
          )}
          
        </AnimatePresence>
      </div>
      
      {/* Call to action */}
      <motion.div 
        className="mt-16 md:mt-24 bg-gradient-to-r from-green-600 to-emerald-700 py-12 md:py-16 relative overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.9 }}
      >
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzBoLTZWMGg2djMwem0tNiAwSDI0VjBoNnYzMHoiLz48cGF0aCBkPSJNMzAgMzZWMjRIMHY2aDMwem0wLTZWMjRoMzB2NkgzMHoiLz48L2c+PC9nPjwvc3ZnPg==')]"></div>
        
        <div className="max-w-6xl mx-auto px-4 relative z-10 text-center">
          <motion.h2 
            className="text-3xl md:text-4xl font-bold mb-4"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Ready to Build Your Football Dynasty?
          </motion.h2>
          <motion.p 
            className="text-lg md:text-xl text-white/90 mb-8 max-w-2xl mx-auto"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            Join thousands of managers already competing for glory across the footballing world
          </motion.p>
          
          <motion.div 
            className="flex flex-col sm:flex-row gap-4 justify-center"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <button
              onClick={() => navigate('/register')} 
              className="px-8 py-3 bg-white text-green-700 rounded-lg font-medium transition-colors shadow-lg hover:bg-white/90"
            >
              Create Account
            </button>
            <button
              onClick={() => navigate('/login')} 
              className="px-8 py-3 bg-green-800/50 hover:bg-green-800/70 text-white rounded-lg font-medium transition-colors border border-white/20"
            >
              Log In
            </button>
          </motion.div>
        </div>
      </motion.div>
      
      <Footer />
    
      </div>
  );
};

export default About;